import { useEffect, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { AdminConnector } from '../../api/types';
import {
  CONNECTOR_CLIENT_ID_LABEL,
  CONNECTOR_PANEL_TITLE,
  CONNECTOR_SECRET_LABEL,
  CONNECTOR_UNSET,
  CONNECTOR_URL_LABEL,
} from '../../lib/copy';

/**
 * **The three strings an Owner pastes into claude.ai** — a tab that reads them
 * when it opens and shows them.
 *
 * It used to be a paragraph and a Show button (owner ruling, 2026-09-10: remove
 * both). The reveal was arguing for a secrecy it did not have: the values cross
 * the wire over the same authenticated read whether a button is clicked or not,
 * and the only person who reaches this tab has already navigated to an
 * organization to reach it. What it actually bought was a click on every visit
 * and a state machine to hold it. The client secret is display-only in any case
 * — nothing in the server authenticates with it, and the token that comes back
 * from Auth0 is what is checked.
 *
 * There is no org in this: one Auth0 client serves every organization, and what
 * a member may see through the connector is their membership, checked per call.
 * It is a tab on the org page because that is where an operator is standing when
 * somebody asks how to connect.
 */
export function ConnectorPanel() {
  const [shown, setShown] = useState<AdminConnector | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .connector()
      .then((answer) => {
        if (live) setShown(answer);
      })
      .catch((caught: unknown) => {
        if (live) setFailed(errorLine(caught));
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="section">
      <div className="section-head">
        <h2>{CONNECTOR_PANEL_TITLE}</h2>
      </div>
      {shown ? <ConnectorStrings connector={shown} /> : null}
      {/* Neither answered yet. One quiet word, in the same place every other
          read on this surface says it. */}
      {!shown && !failed ? <div className="empty-line">Loading.</div> : null}
      {failed ? (
        <div className="flag row">
          <div className="label">Could not read them</div>
          <div className="detail">{failed}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The three values, once fetched — a pure component so a spec can render it from
 * a fixture without a DOM or a click.
 *
 * Each value is in a `<code>` so it selects as one run of text: there is no
 * clipboard code anywhere in these apps, and a monospace field the operator
 * double-clicks is what "copyable" means here. A `null` is a value this
 * deployment has not mounted, and it says so rather than showing an empty box
 * somebody would paste.
 */
export function ConnectorStrings({ connector }: { connector: AdminConnector }) {
  const lines: [string, string | null][] = [
    [CONNECTOR_URL_LABEL, connector.url],
    [CONNECTOR_CLIENT_ID_LABEL, connector.client_id],
    [CONNECTOR_SECRET_LABEL, connector.client_secret],
  ];
  return (
    <table className="table spend-table connector-table">
      <tbody>
        {lines.map(([label, value]) => (
          <tr key={label}>
            <td>{label}</td>
            <td>
              {value === null ? (
                <span className="quiet-cell">{CONNECTOR_UNSET}</span>
              ) : (
                <code>{value}</code>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
